import * as React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, IActionMenuItem } from 'rewire-ui';
import classNames from 'classnames';
import { Forwardburger as ForwardburgerIcon, Backburger as BackburgerIcon } from 'mdi-material-ui';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import OptionsActionMenu from '../ui/OptionsActionMenu';
import ViewTabs, { IViewTab } from '../ui/ViewTabs';
import { InfoPanelContext } from '../ui/InfoPanel';
import { withStyles } from '../utility/styles';

const styles = (_theme: Theme) => ({
  infoPanelHeaderContainer: {
    padding: '10px 15px 0px 15px',
    marginBottom: '10px',
    overflowX: 'hidden',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  infoPanelHeaderTopContainer: {
    overflowX: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  infoPanelHeaderTitleContainer: {
    marginRight: '10px',
  },
  infoPanelHeaderSubTitleContainer: {
    marginTop: '2px',
  },
  infoPanelHeaderActionsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '28px',
    '& > *': {
      marginRight: '15px',
    },
    '& > :last-child': {
      marginRight: '0px',
    },
  },
  infoPanelHeaderTabsContainer: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  infoPanelHeaderTabsDivider: {
    marginLeft: '-20px',
    marginRight: '-20px',
  },
  infoPanelHeaderContainerMinimized: {
    alignItems: 'center',
    padding: '15px 0px',
    marginBottom: '0px',
  },
});

interface IInfoPanelHeaderProps {
  classes?: any;
  title?: string | JSX.Element;
  subTitle?: string | JSX.Element;
  optionsMenuTitle?: string;
  optionsMenuTooltip?: string;
  optionsItems?: IActionMenuItem[];
  otherActions?: JSX.Element;
  tabs?: IViewTab[];
}

type InfoPanelHeaderProps = WithStyle<ReturnType<typeof styles>, IInfoPanelHeaderProps>;

class InfoPanelHeader extends React.Component<InfoPanelHeaderProps> {
  constructor(props: InfoPanelHeaderProps) {
    super(props);
  }

  shouldComponentUpdate(nextProps: any) {
    return (
      this.props.title !== nextProps.title ||
      this.props.subTitle !== nextProps.subTitle ||
      this.props.optionsMenuTitle !== nextProps.optionsMenuTitle ||
      this.props.optionsMenuTooltip !== nextProps.optionsMenuTooltip ||
      this.props.optionsItems !== nextProps.optionsItems ||
      this.props.otherActions !== nextProps.otherActions ||
      this.props.tabs !== nextProps.tabs
    );
  }

  renderHeaderTitle = React.memo((props: { classes: any; title?: string | JSX.Element }) => {
    const { classes, title } = props;

    return (
      <Observe
        render={() =>
          title ? (
            <div className={classes.infoPanelHeaderTitleContainer}>
              {title && typeof title === 'string' ? <Typography variant="h5">{title}</Typography> : title}
            </div>
          ) : null
        }
      />
    );
  });

  renderHeaderSubTitle = React.memo((props: { classes: any; subTitle?: string | JSX.Element }) => {
    const { classes, subTitle } = props;

    return (
      <Observe
        render={() =>
          subTitle ? (
            <div className={classes.infoPanelHeaderSubTitleContainer}>
              {subTitle && typeof subTitle === 'string' ? <Typography>{subTitle}</Typography> : subTitle}
            </div>
          ) : null
        }
      />
    );
  });

  renderTabs = React.memo((props: { classes: any; tabs?: IViewTab[] }) => {
    const { classes, tabs } = props;

    if (!tabs) {
      return null;
    }

    return (
      <>
        <div className={classes.infoPanelHeaderTabsContainer}>
          <ViewTabs tabs={tabs} />
        </div>
        <Divider className={classes.infoPanelHeaderTabsDivider} />
      </>
    );
  });

  render() {
    const { classes, title, subTitle, tabs, optionsMenuTitle, optionsMenuTooltip, optionsItems, otherActions } = this.props;
    const {
      infoPanelHeaderContainer,
      infoPanelHeaderTopContainer,
      infoPanelHeaderActionsContainer,
      infoPanelHeaderContainerMinimized,
      ...optionsActionMenuClasses
    } = classes;

    return (
      <InfoPanelContext.Consumer>
        {({ isOpenInfoPanel, openInfoPanel, closeInfoPanel }) => (
          <Observe
            render={() => (
              <div className={classNames(infoPanelHeaderContainer, !isOpenInfoPanel ? infoPanelHeaderContainerMinimized : '')}>
                {isOpenInfoPanel ? (
                  <>
                    <div className={infoPanelHeaderTopContainer}>
                      <this.renderHeaderTitle classes={classes} title={title} />
                      <div className={infoPanelHeaderActionsContainer}>
                        {otherActions}
                        {optionsItems?.length ? (
                          <OptionsActionMenu
                            title={optionsMenuTitle}
                            tooltip={optionsMenuTooltip}
                            items={optionsItems}
                            classes={optionsActionMenuClasses}
                          />
                        ) : null}
                        <IconButton onClick={closeInfoPanel}>
                          <ForwardburgerIcon />
                        </IconButton>
                      </div>
                    </div>
                    <this.renderHeaderSubTitle classes={classes} subTitle={subTitle} />
                    <this.renderTabs classes={classes} tabs={tabs} />
                  </>
                ) : (
                  <div className={infoPanelHeaderActionsContainer}>
                    <IconButton onClick={openInfoPanel}>
                      <BackburgerIcon />
                    </IconButton>
                  </div>
                )}
              </div>
            )}
          />
        )}
      </InfoPanelContext.Consumer>
    );
  }
}

export default withStyles<IInfoPanelHeaderProps>(styles, InfoPanelHeader);
